import { useState } from 'react';
import { t, type MessageKey } from '../../i18n';
import type { LessonStatus } from '../../app/tutorialSession';
import { LESSONS, type Lesson, type LessonId } from '../../tutorial/lessons';

/** Success texts for lessons that praise doing a little more than asked. */
const DONE_PLUS: Partial<Record<LessonId, MessageKey>> = {
  chain: 'tutorial.chain.donePlus',
  crown: 'tutorial.crown.donePlus',
  wild: 'tutorial.wild.donePlus',
  epitaph: 'tutorial.epitaph.donePlus',
};

const paragraphs = (text: string) =>
  text.split('\n\n').map((para, i) => (
    <p key={i} className="coach-text">
      {para}
    </p>
  ));

/** The tutorial's coach, beside the board: the lesson, the task, and how it went. */
export function CoachPanel({
  index,
  lesson,
  status,
  solved,
  onNext,
  onRestart,
  onGoTo,
  onLeave,
}: {
  index: number;
  lesson: Lesson;
  status: LessonStatus;
  solved: readonly string[];
  onNext: () => void;
  onRestart: () => void;
  onGoTo: (index: number) => void;
  onLeave: () => void;
}) {
  const [hint, setHint] = useState(false);
  const id = lesson.id;
  const last = index === LESSONS.length - 1;
  return (
    <section className="coach" aria-labelledby="coach-title">
      <div className="coach-head">
        <span className="mini-label">
          {t('tutorial.lesson', { n: index + 1, total: LESSONS.length })}
        </span>
        <ol className="coach-dots" aria-label={t('tutorial.progress')}>
          {LESSONS.map((l, i) => (
            <li key={l.id}>
              <button
                type="button"
                className={`coach-dot ${i === index ? 'is-current' : ''} ${solved.includes(l.id) ? 'is-solved' : ''}`}
                aria-label={`${t('tutorial.lesson', { n: i + 1, total: LESSONS.length })}: ${t(`tutorial.${l.id}.title`)}${solved.includes(l.id) ? ' ✓' : ''}`}
                aria-current={i === index ? 'step' : undefined}
                onClick={() => onGoTo(i)}
              />
            </li>
          ))}
        </ol>
      </div>
      <h2 id="coach-title" className="coach-title">
        {t(`tutorial.${id}.title`)}
      </h2>
      {paragraphs(t(`tutorial.${id}.body`))}

      {(status.kind === 'playing' || status.kind === 'settling') && (
        <>
          <p className="coach-task">
            <span className="mini-label">{t('tutorial.task')}</span>
            {t(`tutorial.${id}.task`)}
          </p>
          {hint && <p className="coach-hint">{t(`tutorial.${id}.hint`)}</p>}
          <div className="coach-actions">
            <button
              type="button"
              className="btn btn-small"
              aria-expanded={hint}
              onClick={() => setHint((h) => !h)}
            >
              {hint ? t('tutorial.hideHint') : t('tutorial.hint')}
            </button>
            <button type="button" className="btn btn-small" onClick={onRestart}>
              {t('tutorial.restart')}
            </button>
            <button type="button" className="btn btn-small" onClick={onNext}>
              {t('tutorial.skip')}
            </button>
          </div>
        </>
      )}

      {status.kind === 'done' && (
        <div className="coach-result is-success" role="status">
          <p>
            <strong>{t('tutorial.welldone')}</strong>{' '}
            {t(
              status.message === 'donePlus'
                ? (DONE_PLUS[id] ?? `tutorial.${id}.done`)
                : `tutorial.${id}.done`,
              { score: status.score },
            )}
          </p>
          <div className="coach-actions">
            {/* Focus (and, on phones, the page) moves to the way forward. */}
            <button type="button" className="btn btn-primary" autoFocus onClick={onNext}>
              {last ? t('tutorial.finishLessons') : t('tutorial.next')}
            </button>
            <button type="button" className="btn btn-small" onClick={onRestart}>
              {t('tutorial.restart')}
            </button>
          </div>
        </div>
      )}

      {status.kind === 'retry' && (
        <div className="coach-result is-retry" role="alert">
          <p>
            <strong>{t('tutorial.notQuite')}.</strong> {t(`tutorial.${id}.retry`)}
          </p>
          <div className="coach-actions">
            <button type="button" className="btn btn-primary" autoFocus onClick={onRestart}>
              {t('tutorial.tryAgain')}
            </button>
          </div>
        </div>
      )}

      <div className="coach-nav">
        <button
          type="button"
          className="link-button"
          disabled={index === 0}
          onClick={() => onGoTo(index - 1)}
        >
          ← {t('tutorial.previous')}
        </button>
        <button type="button" className="link-button" onClick={onLeave}>
          {t('tutorial.leave')}
        </button>
      </div>
    </section>
  );
}
